import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { StoreThemeTokens } from "./themes/storeThemes";

interface TrustBadgesProps {
  trustData?: {
    show_badges?: boolean;
    badge1_title?: string;
    badge1_subtitle?: string;
    badge2_title?: string;
    badge2_subtitle?: string;
    badge3_title?: string;
    badge3_subtitle?: string;
    badge4_title?: string;
    badge4_subtitle?: string;
  };
  themeTokens: StoreThemeTokens;
}

const TrustBadges: React.FC<TrustBadgesProps> = ({ trustData, themeTokens }) => {
  const show =
    trustData?.show_badges !== undefined ? Boolean(trustData.show_badges) : true;

  if (!show) return null;

  const badges = [
    {
      icon: <LocalShippingOutlinedIcon sx={{ fontSize: 26, color: themeTokens.accent }} />,
      title: trustData?.badge1_title || "Free Delivery",
      subtitle: trustData?.badge1_subtitle || "All Over India",
    },
    {
      icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 26, color: themeTokens.accent }} />,
      title: trustData?.badge2_title || "Quality Checked",
      subtitle: trustData?.badge2_subtitle || "100% Tested Products",
    },
    {
      icon: <SupportAgentOutlinedIcon sx={{ fontSize: 26, color: themeTokens.accent }} />,
      title: trustData?.badge3_title || "Quick Support",
      subtitle: trustData?.badge3_subtitle || "Instant on WhatsApp",
    },
    {
      icon: <AutorenewOutlinedIcon sx={{ fontSize: 26, color: themeTokens.accent }} />,
      title: trustData?.badge4_title || "Easy Returns",
      subtitle: trustData?.badge4_subtitle || "Hassle-Free Exchange",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        bgcolor: themeTokens.cardBg,
        borderBottom: `1px solid ${themeTokens.cardBorder}`,
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
          {badges.map((badge, idx) => (
            <Grid item xs={6} md={3} key={idx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  transition: "all 0.25s ease",
                  border: `1px solid transparent`,
                  "&:hover": {
                    bgcolor: themeTokens.bgSecondary,
                    borderColor: themeTokens.cardBorder,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 46 },
                    height: { xs: 40, sm: 46 },
                    borderRadius: "50%",
                    bgcolor: themeTokens.accentLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {badge.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: themeTokens.textPrimary,
                      fontSize: { xs: "0.82rem", sm: "0.9rem" },
                      lineHeight: 1.25,
                    }}
                  >
                    {badge.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: themeTokens.textSecondary,
                      fontSize: { xs: "0.72rem", sm: "0.78rem" },
                      mt: 0.25,
                      display: "block",
                    }}
                  >
                    {badge.subtitle}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TrustBadges;
